import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import PrimaryButton from './../../../../../FormElements/PrimaryButton/index';
import Modal from '../../../../../UIelements/Modal';
import * as Styled from '../styles';
import { authenticationPassword, createDeveloperToken } from '../../../../../../services';
import { toast } from 'react-toastify';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../../../../../db/db';
import { FiCopy } from "react-icons/fi";
import Loading from '../../../../../Loading';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { Input } from '@praticabr/ppsa';

type ManualError = {
    type: "manual";
    message: string;
};

const createError = (message: string): ManualError => ({
    type: "manual",
    message
});

type ModalProps = {
    user: any;
    onCancel: () => void;
    onChangePage: (page: string) => void;
}

const developerTokenFormSchema = z.object({
    currentPassword: z.string().min(6, 'user.Preencha a senha atual').nonempty('user.Preencha a senha atual').trim(),
});

type FormDataEditAccount = z.infer<typeof developerTokenFormSchema>;

export const ModalDeveloperToken: React.FC<ModalProps> = (props) => {
    const intl = useIntl();
    const userDb = useLiveQuery(() => db.user.toArray());
    const [isLoading, setIsLoading] = useState(false);
    const { innerWidth } = window;
    const [tokenAccess, setTokenAccess] = useState<{
        isActive: boolean,
        token?: string
    }>({
        isActive: !!props.user.developerToken,
        token: props.user.developerToken
    });

    const { register, handleSubmit, formState: { errors } } = useForm<FormDataEditAccount>()

    const submitDeveloperToken = (data: FormDataEditAccount) => {
        try {
            const userInfo = {
                email: props.user.email,
                password: data.currentPassword
            }
            if (userDb) {
                authenticationPassword(userInfo, userDb[0].accessToken)
                    .then(resp => {
                        createDeveloperToken({ id: props.user.id, email: userInfo.email }, props.user.accessToken)
                            .then(resp => {
                                const developerToken = resp.data.developerToken
                                setTokenAccess({
                                    isActive: true,
                                    token: developerToken
                                })
                                db.user.update(props.user.id, { id: props.user.id, developerToken })
                                setIsLoading(false)
                            })
                    })
                    .catch(error => {
                        console.error(error);
                        toast.error(intl.formatMessage({ id: 'main.Informe sua senha atual corretamente' }).toUpperCase());
                        setIsLoading(false)
                    })
            }
        } catch (error) {
            toast.error(intl.formatMessage({ id: 'menus.Existem campos com informações inválidas' }).toUpperCase());
            setIsLoading(false)
        }
    };

    const handleCopyClick = () => {
        if (tokenAccess.token) {
            if (!navigator.clipboard) {
                const textarea = document.createElement('textarea');
                textarea.value = tokenAccess.token;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                toast.success(intl.formatMessage({ id: 'main.Token copiado com sucesso!' }).toUpperCase());
            } else {
                navigator.clipboard.writeText(tokenAccess.token)
                    .then(() => {
                        toast.success(intl.formatMessage({ id: 'main.Token copiado com sucesso!' }).toUpperCase())
                    })
                    .catch((error) => {
                        console.error(error);
                    })
            }
        }
    };

    const footerDeveloperToken = (
        <>
            <PrimaryButton type='button' size='buttonModal' model='confirmation-gradient-modal' width='108px' onClick={handleSubmit(submitDeveloperToken)} >
                {intl.formatMessage({ id: 'main.CONFIRMAR' })}
            </PrimaryButton>
            <PrimaryButton type='button' size='buttonModal' model='cancellation' width='108px' onClick={() => props.onChangePage("EditAccount")} >
                {intl.formatMessage({ id: 'menus.CANCELAR' })}
            </PrimaryButton>
        </>
    );

    const footerTokenActive = (
        <PrimaryButton type='button' size='buttonModal' model='cancellation' width='108px' onClick={() => props.onChangePage("EditAccount")} >
            {intl.formatMessage({ id: 'tutorial.Fechar' })}
        </PrimaryButton>
    );

    return (
        <Modal
            title={intl.formatMessage({ id: 'main.Token Desenvolvedor' }).toUpperCase()}
            onCancel={props.onCancel}
            footer={(tokenAccess.isActive && innerWidth > 770) ? undefined : (tokenAccess.isActive && innerWidth < 770) ? footerTokenActive : footerDeveloperToken}
            width='650px'
            showButtonClose={tokenAccess.isActive && true}
        >
            {isLoading && <Loading />}
            {!isLoading && (
                <>
                    <Styled.Warning id='developerToken'>
                        {intl.formatMessage({ id: 'main.O Token Desenvolvedor é um código único e intransferível que permite a realização de alterações' })}
                        <br />
                        {intl.formatMessage({ id: 'main.É recomendado que estas alterações sejam realizadas por um profissional da área de tecnologia' })}
                    </Styled.Warning>
                    <Styled.DivToken>
                        {!tokenAccess.isActive || (tokenAccess.token as string).length < 20 ? (
                            <Styled.DivText className='developer-token'>
                                <Input
                                    label={intl.formatMessage({ id: 'main.Para acessar o token, informe sua senha' }) + ' *'}
                                    id='currentPassword'
                                    type='password'
                                    {...register('currentPassword')}
                                    placeholder={intl.formatMessage({ id: 'main.Informe sua senha atual' })}
                                    errors={errors.currentPassword && createError(intl.formatMessage({ id: 'user.Senha incorreta' }))}
                                />
                            </Styled.DivText>
                        ) : (
                            <>
                                <Styled.DivText>
                                    <Styled.Title>{intl.formatMessage({ id: 'main.Seu Token' }) + ':'}</Styled.Title>
                                    <Styled.DivInput className='token' >
                                        <Styled.Input
                                            id='token'
                                            type='input'
                                            placeholder={tokenAccess.token}
                                            readOnly
                                            onClick={handleCopyClick}
                                        />
                                        <FiCopy onClick={handleCopyClick} />
                                    </Styled.DivInput>
                                </Styled.DivText>
                                <Styled.Warning>{intl.formatMessage({ id: 'main.Copie e cole o token na autenticação de todas as requisições.' })}</Styled.Warning>
                            </>
                        )}
                    </Styled.DivToken>
                </>
            )}
        </Modal>
    )
}